import React from 'react';
import {
  formatSubscriptionInterval,
  formatSubscriptionPrice,
  getPublishedSubscriptionPlan,
} from '../../../constants/subscriptionPlans';

const ProfessionalPaymentSection = ({
  promoStatus,
  hasFoundingPerkAvailability = true,
  freeTrialMonths = null,
  accountMode = 'single',
  planName,
  price,
  isPriceLoading = false,
}) => {
  const publishedPlan = getPublishedSubscriptionPlan(accountMode);
  const displayPrice = price || publishedPlan.price;
  const planLabel = planName || publishedPlan.planName;
  // How long the trial runs before the first charge: one month by default,
  // longer with a trial coupon. Founding perks pass null and keep their own copy.
  const freeTrialCopy =
    freeTrialMonths === 1
      ? 'Your first month is free—try Haastia Pro risk-free.'
      : freeTrialMonths > 1
        ? `Your first ${freeTrialMonths} months are free—try Haastia Pro risk-free.`
        : null;
  const perkStatusLabel = () => {
    if (freeTrialCopy) return freeTrialCopy;
    if (!hasFoundingPerkAvailability) return null;
    if (promoStatus?.isLoading) return 'Reserving your founding perk…';
    if (promoStatus?.remaining === 0) return 'Founding perk waitlist';
    if (promoStatus?.remaining != null && promoStatus?.limit != null) {
      return `${promoStatus.remaining} of ${promoStatus.limit} founding spots left`;
    }
    return 'Founding perks are available for a limited time.';
  };
  const formattedPrice = formatSubscriptionPrice(displayPrice);
  const formattedInterval = formatSubscriptionInterval(displayPrice);
  const perkStatus = perkStatusLabel();

  return (
    <section className="provider-payment-card" aria-labelledby="provider-payment-heading">
      <header className="provider-payment-header">
        <div className="provider-payment-icon" aria-hidden="true">💼</div>
        <div className="provider-payment-plan">
          <p className="provider-payment-eyebrow">Haastia Pro</p>
          <h3 id="provider-payment-heading">{planLabel}</h3>
        </div>
        <p className="provider-payment-price" aria-live="polite">
          {formattedPrice && formattedInterval ? (
            <>
              <span className="price-amount">{formattedPrice}</span>
              <span className="price-frequency">/{formattedInterval}</span>
            </>
          ) : (
            <span className="price-loading">
              {isPriceLoading ? 'Loading price…' : 'Price unavailable'}
            </span>
          )}
        </p>
      </header>

      <ul className="provider-payment-benefits">
        <li>
          <span className="benefit-icon" aria-hidden="true">📅</span>
          <span>Showcase your availability so clients can book you instantly.</span>
        </li>
        <li>
          <span className="benefit-icon" aria-hidden="true">📈</span>
          <span>Unlock tools to manage requests and grow your business.</span>
        </li>
        <li>
          <span className="benefit-icon" aria-hidden="true">🛡️</span>
          <span>Secure Stripe-powered billing to protect you and your clients.</span>
        </li>
      </ul>

      <div className="provider-payment-footer">
        <div className="provider-payment-cta" role="note">
          <span className="cta-icon" aria-hidden="true">⚡</span>
          <span>Finish your secure checkout below to activate {planLabel} and start accepting bookings.</span>
        </div>
        {perkStatus && <p className="provider-payment-copy perk-status">{perkStatus}</p>}
      </div>
    </section>
  );
};

export default ProfessionalPaymentSection;
